import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {message} from "antd";
import {AxiosError} from "axios";
import {PaginationMeta} from "../redux/service/i-service";
import {AnalyticsEvent} from "../utils/analytics";
import {useClientV2} from "../utils/hooks";
import {
  normalizeV2CustomerMetrics,
  normalizeV2CustomerMetricsPerCustomer,
  normalizeV2CustomersList,
  normalizeV2PrevPurchases,
} from "./customer/customer-analytics-normalizer";

type TCustomerMetricsApiResponse = {
  totalUniqueCustomers: number;
  totalRevenue: {currency: string; amount: number}[];
  totalCalls: number;
  currency: string;
};

type TCustomerApiResponse = {
  name: string;
  emailAddress: string;
  phoneNumber: string;
  numberOfPurchases: number;
  totalVolume: number;
  revenueByCurrency?: Record<string, number>;
};

/**
 * The service a bundle was bought against, as embedded in a bundle row.
 *
 * Everything the scheduling flow needs to book the next session, so the
 * offering no longer has to be re-fetched from the public service endpoint.
 */
export type TCustomerPaidBundleServiceApiResponse = {
  id: number;
  slug: string;
  name: string;
  callDuration: number;
  audioLocation: boolean;
  videoLocation: boolean;
  /** Raw JSON string as stored on the offering; parsed at the point of display. */
  bookingQuestions: string | null;
};

/**
 * One purchased bundle, as returned by `GET customer/bookings/bundles`.
 *
 * One row per bundle - a bundle whose sessions are all still unbooked appears
 * exactly once and can be scheduled against its own `reference`. Not to be
 * confused with `customer/bookings/upcoming-bundles`, which lists the
 * individual session bookings made against a bundle.
 *
 * Only Active / In Progress / Completed bundles are returned, so there is no
 * status filtering left to do on the client.
 */
export type TCustomerPaidBundleApiResponse = {
  id: number;
  reference: string;
  bookedSessions: number;
  noOfSessions: number;
  price: number;
  currency: string | null;
  noOfInterval: string | null;
  statusId: number;
  statusName: string;
  createdAt: string;
  coachUsername: string | null;
  /** Already resolved server-side: the coach's display name, else their full name. */
  coachDisplayName: string | null;
  service: TCustomerPaidBundleServiceApiResponse | null;
};

type GetCustomersApiResponse = {
  id: string;
  first_name: string;
  last_name: string;
  email_address: string;
  lastPurchaseDate?: string;
  reference?: string;
  statusName: string;
}[];
type GetCustomersPrevPurchasesApiResponse = {
  service: {
    name: string;
    callPriceCurrency: string;
  };
  offeringType?: string;
  date: string;
  amount: number;
  currency: string;
  reference: string;
}[];

const useGetCustomers = (page: number = 1, per_page: number = 10) => {
  const client = useClientV2();

  return useQuery<
    {data: GetCustomersApiResponse; meta: PaginationMeta; total: number},
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: ["customers", page, per_page],
    queryFn: async () => {
      const data = await client(
        `analytics/customer/customers?page=${page}&perPage=${per_page}`
      );
      const payload = data?.data ?? data;
      const normalized = normalizeV2CustomersList(payload);
      const total = normalized.total ?? normalized.transactions.length;
      const totalPages = Math.max(1, Math.ceil(total / per_page));
      const meta: PaginationMeta = {
        current_page: page,
        per_page,
        total_pages: totalPages,
        next_page: normalized.hasNextPage ? page + 1 : 0,
        previous_page: normalized.hasPrevPage ? page - 1 : 0,
      };
      return {data: normalized.transactions, meta, total};
    },
    keepPreviousData: true,
  });
};
const useGetCustomerMetrics = () => {
  const client = useClientV2();

  return useQuery<
    TCustomerMetricsApiResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: ["customer_metrics"],
    queryFn: async () => {
      const data = await client("analytics/customer/metrics");
      const inner = data?.data ?? data;
      return normalizeV2CustomerMetrics(inner);
    },
  });
};

const useGetCustomer = (customerId?: string) => {
  const client = useClientV2();

  return useQuery<TCustomerApiResponse, AxiosError<{status: boolean; message: string}>>({
    queryKey: ["customer", customerId],
    queryFn: async () => {
      if (!customerId) {
        throw new Error("customerId is required");
      }
      const email = decodeURIComponent(customerId);
      const data = await client(
        `analytics/customer/fetch/metrics?email=${encodeURIComponent(email)}`
      );
      const inner = data?.data ?? data;
      const {numberOfPurchases, totalVolume, revenueByCurrency} =
        normalizeV2CustomerMetricsPerCustomer(inner);
      return {
        name: email,
        emailAddress: email,
        phoneNumber: "",
        numberOfPurchases,
        totalVolume,
        revenueByCurrency,
      };
    },
    enabled: !!customerId,
  });
};
const useGetCustomerPrevPurchases = (_mentorId: string, customerEmail?: string) => {
  const client = useClientV2();

  return useQuery<
    GetCustomersPrevPurchasesApiResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: ["customer_prev_purchases", customerEmail],
    queryFn: async () => {
      if (!customerEmail) {
        throw new Error("customerEmail is required");
      }
      const data = await client(
        `analytics/customer/fetch?email=${encodeURIComponent(
          customerEmail.toLowerCase()
        )}`
      );
      const inner = data?.data ?? data;
      const offerings = Array.isArray(inner) ? inner : inner?.data ?? [];
      return normalizeV2PrevPurchases(offerings);
    },
    enabled: !!customerEmail,
  });
};

type ExportCSVParams = {
  startDate?: string;
  endDate?: string;
};

const useExportCSV = () => {
  const client = useClientV2();

  return useMutation({
    mutationFn: (params?: ExportCSVParams) => {
      const year = new Date().getFullYear();
      const startDate = params?.startDate ?? `${year}-01-01`;
      const endDate = params?.endDate ?? `${year}-12-31`;
      return client(
        `analytics/customer/export?startDate=${startDate}&endDate=${endDate}`,
        {method: "GET", responseType: "text"}
      );
    },
    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message);
      AnalyticsEvent("Coach customer", "Coach customer export CSV fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Coach customer", "Coach customer export CSV success");
    },
  });
};

const useGetCustomerPaidBundles = (page = 1, per_page = 5) => {
  const client = useClientV2();

  return useQuery<
    {data: TCustomerPaidBundleApiResponse[]; meta: PaginationMeta; total: number},
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: ["customer_paid_bundles", page],
    queryFn: async () => {
      const response = await client(
        `customer/bookings/bundles?page=${page}&perPage=${per_page}`
      );
      // Standard v2 list envelope: {data: {data, total, hasNextPage, hasPrevPage}}.
      const payload = response?.data ?? response;
      const bundles: TCustomerPaidBundleApiResponse[] = payload?.data ?? [];
      const total = Number(payload?.total ?? bundles.length);
      return {
        data: bundles,
        total,
        meta: {
          current_page: page,
          per_page,
          total_pages: Math.max(1, Math.ceil(total / per_page)),
          // The pager reads 0 as "no such page".
          next_page: payload?.hasNextPage ? page + 1 : 0,
          previous_page: payload?.hasPrevPage ? page - 1 : 0,
        },
      };
    },
    // refetchOnWindowFocus: false,
    keepPreviousData: true,
  });
};

const useCustomerCancelBooking = () => {
  const queryClient = useQueryClient();
  const clientV2 = useClientV2();
  return useMutation({
    mutationFn: (payload: {bookingRef: string; description: string}) =>
      clientV2(`customer/bookings/${payload.bookingRef}/cancel`, {
        method: "PUT",
        data: {reason: payload.description},
      }),
    onMutate: () => {
      const oldBookings = queryClient.getQueryData(["customer_upcoming_calls"]);

      return () => queryClient.setQueryData(["customer_upcoming_calls"], oldBookings);
    },
    onError: (error: AxiosError<{message: string}>, values, rollback) => {
      message.error(error.message);
      AnalyticsEvent("Customer cancel booking", "Customer cancel booking fail");
      if (typeof rollback === "function") {
        rollback();
      }
    },
    onSuccess: () => {
      message.success("The selected booking has been cancelled successfully!");
      AnalyticsEvent("Customer cancel booking", "Customer cancel booking success");
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({queryKey: ["customer_upcoming_calls"]}),
        queryClient.invalidateQueries({queryKey: ["customer_paid_bundles"]}),
      ]),
  });
};

export {
  useGetCustomers,
  useGetCustomerMetrics,
  useGetCustomer,
  useGetCustomerPrevPurchases,
  useExportCSV,
  useGetCustomerPaidBundles,
  useCustomerCancelBooking,
};
